import useFenixShippingEstimates from "@hooks/useFenixShippingEstimates"
import DeliveryText from "@views/common/ProductDetails/DeliveryText"
import ShippingText from "@views/common/ProductDetails/ShippingText"
import moment from "moment"
import React from "react"

type Args = {
  // Always needed
  availabilityIsAvailable?: boolean
  currentSku?: string
  // Fenix params (only used if enableFenixEstimates === true)
  customerZipCode?: string
  // Fenix toggles (US-only PDP vs SOPDP/localized); pass false for SOPDP
  enableFenixEstimates?: boolean
  fenixArgs?: {
    shippingMethods?: string[]
    variants: string[] | VariantLike[] // Accepts raw variants OR array of skus
  }
  shippingMessage?: string
}

type VariantLike = { sku?: null | string } | null | undefined

export function usePdpShipping({
  availabilityIsAvailable,
  currentSku,
  customerZipCode,
  enableFenixEstimates = true,
  fenixArgs,
  shippingMessage,
}: Args) {
  // Normalize variants -> the Fenix hook expects Array<{ sku }>
  const fenixVariants: undefined | VariantLike[] = React.useMemo(() => {
    if (!fenixArgs?.variants) return undefined
    const v = fenixArgs.variants
    // If it's already VariantLike[], pass through
    if (Array.isArray(v) && typeof v[0] === "object") return v as VariantLike[]
    // If it's string[] of SKUs, wrap them
    if (Array.isArray(v)) {
      return (v as string[]).map((sku) => ({ sku }))
    }
    return undefined
  }, [fenixArgs?.variants])

  // Only engage Fenix when explicitly enabled
  const fenixState = useFenixShippingEstimates(
    enableFenixEstimates ? customerZipCode : undefined,
    enableFenixEstimates ? fenixVariants : undefined,
    enableFenixEstimates ? fenixArgs?.shippingMethods : undefined
  )

  const defaultShipping = React.useMemo(
    () => <ShippingText message={shippingMessage ?? ""} />,
    [shippingMessage]
  )

  const [deliveryText, setDeliveryText] = React.useState<React.ReactNode>()
  const [shippingText, setShippingText] =
    React.useState<React.ReactNode>(defaultShipping)

  // Keep baseline shipping text in sync with message changes
  React.useEffect(() => {
    setShippingText(defaultShipping)
  }, [defaultShipping])

  React.useEffect(() => {
    // If not available (or Fenix disabled), show baseline & no ETA
    if (!availabilityIsAvailable || !enableFenixEstimates) {
      setDeliveryText(undefined)
      setShippingText(defaultShipping)
      return
    }

    // If no zip or no Fenix config, we can't show ETA — fallback to baseline
    if (!customerZipCode || !fenixState) {
      setDeliveryText(undefined)
      setShippingText(defaultShipping)
      return
    }

    // Surface ZIP-specific errors *instead of* the baseline message
    if (fenixState.zipCodeError) {
      setDeliveryText(undefined)
      setShippingText(
        <ShippingText message={fenixState.zipCodeErrorMessage ?? ""} />
      )
      return
    }

    const shippingData = fenixState.getShippingDataForSku?.(currentSku)
    if (shippingData) {
      const deliveryDate = moment(shippingData.guaranteedDeliveryDate).format(
        "ddd, MMM D,"
      )
      const orderWithin = `${shippingData.hours}hr ${shippingData.minutes}min`

      // Keep the shipping message, but add an ETA line
      setShippingText(<ShippingText message={shippingMessage ?? ""} />)
      setDeliveryText(
        <DeliveryText deliveryDate={deliveryDate} orderWithin={orderWithin} />
      )
    } else {
      setDeliveryText(undefined)
      setShippingText(defaultShipping)
    }
  }, [
    availabilityIsAvailable,
    enableFenixEstimates,
    customerZipCode,
    fenixState?.getShippingDataForSku,
    fenixState?.zipCodeError,
    fenixState?.zipCodeErrorMessage,
    currentSku,
    defaultShipping,
    shippingMessage,
  ])

  return {
    deliveryText,
    // If Fenix loading/error states are needed upstream:
    fenixData: fenixState && {
      invalidShippingData: fenixState.invalidShippingData,
      shippingDataLoaded: fenixState.shippingDataLoaded,
      zipCodeError: fenixState.zipCodeError,
      zipCodeErrorMessage: fenixState.zipCodeErrorMessage,
    },
    shippingText,
  }
}

export default usePdpShipping
